import "./lib/i18n";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { loadRuntimeConfig } from "@/lib/runtime-config";
import { initSentry } from "@/lib/monitoring/sentry";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found in index.html");

// Prime the runtime config cache before rendering. `getRuntimeConfigSync`
// is consumed synchronously by code that can't await (notably map-utils →
// resolveMapStyle, which uses getRuntimeConfigSync to build the style URL);
// without this priming step it returns the build-time defaults
// (http://localhost:8585), and the prod-deployed SPA tries to fetch tiles
// from localhost. The fetch is one round-trip (≤200ms typical) to the
// same origin, served by nginx's static runtime-config.json — fast and
// safe to block first paint on.
void loadRuntimeConfig().then((config) => {
  // Init Sentry with the resolved runtime config (DSN + enable flag come from
  // /runtime-config in prod, build-time env locally). No-op when disabled;
  // loads the SDK in parallel with the first render (not awaited).
  void initSentry(config);

  createRoot(rootEl).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
});
